import { CSSProperties } from 'react'
import { IconType } from 'react-icons'

import styles from './MarkerBadge.module.css'

/** Teardrop outline, and the inset path the white ring is stroked along. */
const PIN_BODY =
  'M51 24C51 42 27 60 27 60C27 60 3 42 3 24C3 17.6348 5.52856 11.5303 10.0294 7.02944C14.5303 2.52856 20.6348 0 27 0C33.3652 0 39.4697 2.52856 43.9706 7.02944C48.4714 11.5303 51 17.6348 51 24Z'
const PIN_RING =
  'M49.5 24C49.5 32.4075 43.8443 41.0424 37.8789 47.7534C34.9314 51.0694 31.9792 53.8376 29.7622 55.7774C28.6548 56.7464 27.7336 57.5063 27.0917 58.0221C27.0604 58.0472 27.0299 58.0717 27 58.0957C26.9701 58.0717 26.9396 58.0472 26.9083 58.0221C26.2664 57.5063 25.3452 56.7464 24.2378 55.7774C22.0208 53.8376 19.0686 51.0694 16.1211 47.7534C10.1557 41.0424 4.5 32.4075 4.5 24C4.5 18.0326 6.87053 12.3097 11.0901 8.0901C15.3097 3.87053 21.0326 1.5 27 1.5C32.9674 1.5 38.6903 3.87053 42.9099 8.0901C47.1295 12.3097 49.5 18.0326 49.5 24Z'

export type MarkerBadgeProps = {
  /** Any react-icons glyph. Rendered white and centred inside the marker. */
  glyph: IconType
  /** Fill colour of the disc or pin. */
  color: string
  /**
   * `circle` is a plain disc, sized square. `pin` is a teardrop with a white ring and a
   * drop shadow, keeping the artwork's 54:66 ratio — so `size` sets its width only.
   */
  shape?: 'circle' | 'pin'
  size?: number | string
  /** Draws a 2px white ring around the disc. Circles only; pins always carry their ring. */
  border?: boolean
  /** Fills the glyph instead of stroking it, for glyphs meant to read as solid. */
  filled?: boolean
  /** Rotates the glyph by this many degrees, animated. */
  rotate?: number
  className?: string
}

/**
 * A coloured marker with a glyph centred on it, used for map markers and their legends.
 */
export const MarkerBadge = ({
  glyph: Glyph,
  color,
  shape = 'circle',
  size = 20,
  border,
  filled,
  rotate,
  className,
}: MarkerBadgeProps) => {
  const isPin = shape === 'pin'

  return (
    <span
      className={[
        styles.badge,
        isPin ? styles.pin : styles.circle,
        border && !isPin && styles.bordered,
        filled && styles.filled,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        {
          width: size,
          ...(isPin ? {} : { height: size, backgroundColor: color }),
          ...(rotate != null ? { '--rotation': `${rotate}deg` } : {}),
        } as CSSProperties
      }
    >
      {isPin && (
        <svg className={styles.pinShape} viewBox="0 0 54 66" fill="none" aria-hidden="true">
          <path d={PIN_BODY} fill={color} />
          <path d={PIN_RING} stroke="#fff" strokeWidth="3" />
        </svg>
      )}
      <Glyph />
    </span>
  )
}
